import apiClient from "../../api/apiClient";

export interface BmsBooking {
  id: number;
  booking_number: string;
  status: string;
  resource_user_id: number;
  service_id?: number;
  customer_id?: number;
  resource_name?: string;
  service_name?: string;
  customer_name?: string;
  customer_surname?: string;
  customer_phone?: string;
  start_at: string;
  end_at: string;
  timezone: string;
  cancellation_reason?: string | null;
}

export interface BmsCalendarAvailability {
  start_at: string;
  end_at: string;
  break_start_at?: string | null;
  break_end_at?: string | null;
  days: number[];
}

export interface BmsCalendarEvent {
  id: number;
  booking_number: string;
  status: string;
  service_name: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  start_at: string;
  end_at: string;
  timezone: string;
}

export interface BmsCalendarResource {
  resource: {
    resource_user_id: number;
    resource_name: string;
  };
  availability: BmsCalendarAvailability[];
  events: BmsCalendarEvent[];
}

export interface BmsUser {
  id: number;
  company_user_id: number;
  display_name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface BmsCustomer {
  id: number;
  name: string;
  surname: string;
  phone: string;
  email?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface BmsServiceItem {
  id: number;
  name: string;
  description?: string;
  duration_minutes: number;
  is_active: number;
}

export interface BmsAvailability {
  id: number;
  user_id: number;
  start_at: string;
  end_at: string;
  break_start_at?: string | null;
  break_end_at?: string | null;
  days: string;
}

export interface BmsUserService {
  id: number;
  user_id: number;
  service_id: number;
  duration_override?: number | null;
  is_active: number;
  service_name?: string;
  service_description?: string;
}

const bmsService = {
  // Bookings
  listBookings: async (
    chatbotId: number,
    params?: {
      skip?: number;
      limit?: number;
      status?: string;
      q?: string;
      service_id?: number;
      customer_id?: number;
      customer_phone?: string;
      date_from?: string;
      date_to?: string;
      resource_user_id?: number;
      start_at?: string;
      end_at?: string;
    }
  ): Promise<{ data: BmsBooking[]; total: number; skip: number; limit: number }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/bookings`, { params }),

  createBooking: async (
    chatbotId: number,
    payload: {
      resource_user_id: number;
      service_id: number;
      customer_name: string;
      customer_surname: string;
      customer_phone: string;
      customer_email?: string;
      start_at: string;
      end_at: string;
      timezone: string;
      status?: string;
    }
  ): Promise<{ booking_number: string; status: string; phone_last4_hint: string }> =>
    apiClient.post(`/bms/chatbots/${chatbotId}/bookings`, payload),

  updateBooking: async (
    chatbotId: number,
    bookingId: number,
    payload: {
      status?: string;
      start_at?: string;
      end_at?: string;
      timezone?: string;
      resource_user_id?: number;
      service_id?: number;
      customer_id?: number;
      cancellation_reason?: string;
    }
  ): Promise<{ data: BmsBooking }> =>
    apiClient.put(`/bms/chatbots/${chatbotId}/bookings/${bookingId}`, payload),

  // Calendar
  getCalendar: async (
    chatbotId: number,
    params: { start_at: string; end_at: string }
  ): Promise<{ resources: BmsCalendarResource[]; start_at: string; end_at: string }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/calendar`, { params }),

  listBmsEnabledChatbots: async (params?: {
    company_id?: number;
  }): Promise<{ data: Array<{ id: number; name: string; company_id: number }> }> =>
    apiClient.get("/bms/chatbots/bms-enabled", { params }),

  // Users
  listUsers: async (chatbotId: number, params?: { skip?: number; limit?: number }): Promise<{ data: BmsUser[]; total: number; skip: number; limit: number }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/users`, { params }),

  listAvailableCompanyUsers: async (
    chatbotId: number
  ): Promise<{ data: Array<{ company_user_id: number; full_name: string; email: string; company_role?: string }> }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/users/available-company-users`),

  addUser: async (chatbotId: number, company_user_id: number): Promise<any> =>
    apiClient.post(`/bms/chatbots/${chatbotId}/users`, { company_user_id }),

  deactivateUser: async (chatbotId: number, bmsUserId: number): Promise<any> =>
    apiClient.delete(`/bms/chatbots/${chatbotId}/users/${bmsUserId}`),

  // User Availability
  getUserAvailability: async (chatbotId: number, bmsUserId: number): Promise<{ data: BmsAvailability[] }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/users/${bmsUserId}/availability`),

  createAvailability: async (
    chatbotId: number,
    payload: { user_id: number; start_at: string; end_at: string; break_start_at?: string; break_end_at?: string; days: number[] }
  ): Promise<any> =>
    apiClient.post(`/bms/chatbots/${chatbotId}/availability`, payload),

  deleteAvailability: async (chatbotId: number, availabilityId: number): Promise<any> =>
    apiClient.delete(`/bms/chatbots/${chatbotId}/availability/${availabilityId}`),

  updateAvailability: async (
    chatbotId: number,
    availabilityId: number,
    payload: { start_at?: string; end_at?: string; break_start_at?: string; break_end_at?: string; days?: number[] }
  ): Promise<{ data: BmsAvailability }> =>
    apiClient.put(`/bms/chatbots/${chatbotId}/availability/${availabilityId}`, payload),

  // User Services
  getUserServices: async (chatbotId: number, bmsUserId: number): Promise<{ data: BmsUserService[] }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/users/${bmsUserId}/services`),

  assignUserService: async (
    chatbotId: number,
    payload: { user_id: number; service_id: number; duration_override?: number; is_active?: boolean }
  ): Promise<any> =>
    apiClient.post(`/bms/chatbots/${chatbotId}/user-services`, payload),

  updateUserService: async (
    chatbotId: number,
    userServiceId: number,
    payload: { duration_override?: number | null; is_active?: boolean; clear_duration_override?: boolean }
  ): Promise<any> =>
    apiClient.put(`/bms/chatbots/${chatbotId}/user-services/${userServiceId}`, payload),

  removeUserService: async (chatbotId: number, userServiceId: number): Promise<any> =>
    apiClient.delete(`/bms/chatbots/${chatbotId}/user-services/${userServiceId}`),

  // Services
  listServices: async (chatbotId: number, params?: { skip?: number; limit?: number }): Promise<{ data: BmsServiceItem[]; total: number; skip: number; limit: number }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/services`, { params }),

  createService: async (
    chatbotId: number,
    payload: { name: string; description?: string; duration_minutes: number; is_active?: boolean }
  ): Promise<any> =>
    apiClient.post(`/bms/chatbots/${chatbotId}/services`, payload),

  updateService: async (
    chatbotId: number,
    serviceId: number,
    payload: { name?: string; description?: string; duration_minutes?: number; is_active?: boolean }
  ): Promise<any> =>
    apiClient.put(`/bms/chatbots/${chatbotId}/services/${serviceId}`, payload),

  deleteService: async (chatbotId: number, serviceId: number): Promise<any> =>
    apiClient.delete(`/bms/chatbots/${chatbotId}/services/${serviceId}`),

  // Customers
  listCustomers: async (chatbotId: number, params?: { skip?: number; limit?: number }): Promise<{ data: BmsCustomer[]; total: number; skip: number; limit: number }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/customers`, { params }),

  updateCustomer: async (
    chatbotId: number,
    customerId: number,
    payload: { name?: string; surname?: string; phone?: string; email?: string; status?: string }
  ): Promise<{ data: BmsCustomer }> =>
    apiClient.put(`/bms/chatbots/${chatbotId}/customers/${customerId}`, payload),

  // Expert Profile
  getMyExpertProfile: async (chatbotId: number): Promise<{ data: BmsUser | null }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/my-expert-profile`),

  // Booking Settings
  getBookingSettings: async (chatbotId: number): Promise<{ data: any }> =>
    apiClient.get(`/bms/chatbots/${chatbotId}/booking-settings`),

  updateBookingSettings: async (chatbotId: number, payload: any): Promise<{ data: any }> =>
    apiClient.put(`/bms/chatbots/${chatbotId}/booking-settings`, payload),
};

export default bmsService;
